import { BrandLogo } from "@albelli/arc-3-react/lib/BrandLogo";
import { Grid } from "@albelli/arc-3-react/lib/Grid";
import { Loader } from "@albelli/arc-3-react/lib/Loader/Loader";
import { requestMenu } from "application/menu/menuActions";
import classnames from "classnames";
import { isLoading } from "helpers/requestStateHelper";
import { isNotRequested } from "helpers/requestStateHelper.js";
import { vendor } from "helpers/vendorHelper";
import debounce from "lodash.debounce";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { MenuContext, useMenuContext } from "./MenuContext";
import { MenuLink } from "./MenuLink";

export const MenuItem = ({ hideMobile, hideDesktop, ...props }) => {
  const liClasses = classnames("arc3-menu__dropdown", {
    "arc3-hide-for-small": hideMobile,
    "arc3-hide-for-medium-up": hideDesktop
  });

  return (
    <li className={liClasses} data-cy={props.items && props.items.length ? "MenuItemWithItems" : "MenuItem"}>
      <MenuLink {...props} />
      <MenuList {...props} style={{ display: props.expanded === props.text ? "block" : "" }} />
    </li>
  );
};

export const MenuList = ({ items, id, style }) => {
  const menuContext = useMenuContext();

  if (!!items && items.length)
    return (
      <ul className="arc3-menu__dropdown-content arc3-typo-ul-no-style " id={id} style={style}>
        {items
          .filter(i => menuContext.isAuthenticated || (!menuContext.isAuthenticated && !i.isLogout))
          .map((i, j) => (
            <MenuItem key={i.text} secondary={true} {...i} />
          ))}
      </ul>
    );
  return null;
};

const Menu = ({ header, menu, ...props }) => {
  const mainUrl = useSelector(state => state.menu.items.routes.Main);
  //The two menus require check boxes and special handling for in mobile view..
  const mainMenuId = "main-menu";
  const accountMenuId = "account-menu"; //Must be set in menu.json
  //un-check the check boxes on screen rotate/resize
  const mainMenuCheckBox = useRef(null);
  const accountMenuCheckBox = useRef(null);
  const handleResize = debounce(() => {
    mainMenuCheckBox.current.checked = false;
    accountMenuCheckBox.current.checked = false;
  }, 133);
  //
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  //
  const MenuBar = ({ header, ...props }) => {
    return (
      <>
        <input
          type="checkbox"
          ref={mainMenuCheckBox}
          className={`arc3-hide-for-medium-up arc3-menu-toggle ${mainMenuId}`}
        />
        <input
          type="checkbox"
          ref={accountMenuCheckBox}
          className={`arc3-hide-for-medium-up arc3-menu-toggle ${accountMenuId}`}
        />
        <div className="arc3-menu__bar" {...props}>
          <div className="arc3-container">
            <div className="arc3-menu__bar__mobile-btn arc3-hide-for-medium-up">
              <FormattedMessage id="menu.menu" defaultMessage="MENU" />
            </div>
            <div className="arc3-hide-for-medium-up" href="{#}">
              <BrandLogo
                className="arc3-menu__bar__logo"
                vendor={vendor}
                href={mainUrl}
                white
                small
                data-cy="MainBrandLogo"
              />
            </div>
            {!!header && header.length && (
              <ul className="arc3-menu__bar__nav arc3-typo-ul-no-style">
                {header.map((i, j) => (
                  <MenuItem key={i.text} {...i} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
    );
  };

  const SubMenu = ({ menu, ...props }) => {
    //only one menu item expanded at a time per subMenu
    const [expanded, setExpanded] = useState({ name: "", time: "" });
    const handleResize = debounce(() => {
      setExpanded(false);
    }, 133);
    useEffect(() => {
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    return (
      <div className="arc3-container" {...props}>
        <div className="arc3-menu__main">
          <Grid cell column={3} columnDesktop={2} columnTablet={3} hideSmall className="arc3-menu__main__logo">
            <BrandLogo vendor={vendor} href={mainUrl} data-cy="MainBrandLogo" />
          </Grid>
          <Grid cell column={12} columnDesktop={10}>
            {!!menu && menu.length && (
              <ul className="arc3-menu__main__nav arc3-typo-ul-no-style" id={`${mainMenuId}`}>
                {menu.map((i, j) => (
                  <MenuItem
                    key={j}
                    secondary={true}
                    showArrow={true}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    {...i}
                  />
                ))}
              </ul>
            )}
          </Grid>
        </div>
      </div>
    );
  };

  return (
    <div data-cy="Menu" {...props}>
      <div className="arc3-menu">
        <MenuBar header={header} data-cy="MenuBar" />
        <SubMenu menu={menu} data-cy="SubMenu" />
      </div>
    </div>
  );
};

export const Header = ({ isAuthenticated }) => {
  const menu = useSelector(state => state.menu);
  const dispatch = useDispatch();
  useEffect(() => {
    if (isNotRequested(menu)) {
      dispatch(requestMenu());
    }
  }, []);

  const { items } = menu;
  if (isLoading(menu) || !items || !items.header) return <Loader />;
  return (
    <MenuContext.Provider value={{ isAuthenticated }}>
      <Menu header={items.header} menu={items.menu} />
    </MenuContext.Provider>
  );
};
